import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { RemiseRecusPartiels } from '../recus/RemiseRecusPartiels';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Receipt, RefreshCw, Clock, CheckCircle, AlertTriangle, TrendingUp, FileEdit,
  Search, Filter, ChevronDown, ChevronUp, FileText, Euro, LayoutDashboard,
  PieChart, Sparkles, Wallet, Banknote, BarChart3, Building, X
} from 'lucide-react';

export default function HeaderSection({
  stats, dashboardStats, refreshing, fetchData,
  searchTerm, setSearchTerm,
  filterType, setFilterType,
  filterMontantMin, setFilterMontantMin,
  filterMontantMax, setFilterMontantMax,
  filterDateDebut, setFilterDateDebut,
  filterDateFin, setFilterDateFin,
  filterRecuStatus, setFilterRecuStatus,
  showAdvancedFilters, setShowAdvancedFilters,
  resetFilters,
  activeTab, setActiveTab,
  documentsEnAttente, documentsValides,
  getAllAcomptesESS,
}) {
  return (
    <>
      {/* Header avec titre - Design amélioré */}
      <div className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-gray-100 rounded-2xl p-6 border">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-200/50">
            <Receipt className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Factures & Reçus ESS
            </h2>
            <p className="text-gray-500 mt-0.5">Validation des paiements et génération des reçus</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <RemiseRecusPartiels />
          <Button 
            onClick={() => fetchData(true)} 
            variant="outline"
            disabled={refreshing}
            className="gap-2 shadow-sm hover:shadow-md transition-shadow"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Statistiques - Design amélioré */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 text-white shadow-xl shadow-orange-200/50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5 relative">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">En attente</p>
                <p className="text-4xl font-black mt-1">{stats.enAttente}</p>
                {stats.avenantEnAttente > 0 && (
                  <p className="text-xs text-amber-200 mt-2 flex items-center gap-1">
                    <FileEdit className="h-3 w-3" />
                    {stats.avenantEnAttente} avenant(s)
                  </p>
                )}
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Clock className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white shadow-xl shadow-emerald-200/50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5 relative">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Validés</p>
                <p className="text-4xl font-black mt-1">{stats.valides}</p>
                <p className="text-xs text-emerald-200 mt-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Paiements reçus
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-500 via-red-500 to-pink-600 text-white shadow-xl shadow-rose-200/50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5 relative">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-rose-100 text-sm font-medium">À encaisser</p>
                <p className="text-3xl font-black mt-1">
                  {stats.montantEnAttente.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                </p>
                <p className="text-xs text-rose-200 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  En attente de virement
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Euro className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-600 text-white shadow-xl shadow-blue-200/50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5 relative">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Encaissé</p>
                <p className="text-3xl font-black mt-1">
                  {stats.montantValide.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                </p>
                <p className="text-xs text-blue-200 mt-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Montant total reçu
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Banknote className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Barre de recherche avancée - Design amélioré */}
      <Card className="border shadow-sm">
        <CardContent className="p-5">
          <div className="space-y-4">
            {/* Recherche principale */}
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Rechercher par raison sociale, référence, SIRET, email, territoire, montant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 text-lg border-2 focus:border-blue-500 rounded-xl"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:bg-gray-100 rounded-full"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Filtre type */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-44 h-12 border-2 rounded-xl">
                  <FileText className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="contrat">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      Dossiers
                    </span>
                  </SelectItem>
                  <SelectItem value="avenant">
                    <span className="flex items-center gap-2">
                      <FileEdit className="h-4 w-4 text-purple-500" />
                      Avenants
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Toggle filtres avancés */}
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="h-12 gap-2"
              >
                <Filter className="h-4 w-4" />
                Filtres
                {showAdvancedFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {/* Reset */}
              {(searchTerm || filterType !== 'all' || filterMontantMin || filterMontantMax) && (
                <Button variant="ghost" onClick={resetFilters} className="h-12">
                  <X className="h-4 w-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>

            {/* Filtres avancés */}
            {showAdvancedFilters && (
              <div className="space-y-4 pt-4 border-t">
                {/* Ligne 1 - Montants et Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-500 whitespace-nowrap">Montant Global</Label>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filterMontantMin}
                      onChange={(e) => setFilterMontantMin(e.target.value)}
                      className="w-28"
                    />
                    <span className="text-gray-400">-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filterMontantMax}
                      onChange={(e) => setFilterMontantMax(e.target.value)}
                      className="w-28"
                    />
                    <span className="text-gray-500">€</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-500 whitespace-nowrap">Période</Label>
                    <DatePickerFr
                      value={filterDateDebut}
                      onChange={setFilterDateDebut}
                      className="w-36"
                    />
                    <span className="text-gray-400">→</span>
                    <DatePickerFr
                      value={filterDateFin}
                      onChange={setFilterDateFin}
                      className="w-36"
                    />
                  </div>
                </div>
                
                {/* Ligne 2 - Filtres Reçus */}
                <div className="flex flex-wrap items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-emerald-600" />
                    <Label className="text-sm font-semibold text-emerald-800">Filtrer par reçus:</Label>
                  </div>
                  <Select value={filterRecuStatus} onValueChange={setFilterRecuStatus}>
                    <SelectTrigger className="w-[220px] bg-white border-emerald-200">
                      <SelectValue placeholder="Tous les reçus" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">📋 Tous les documents</SelectItem>
                      <SelectItem value="recu_echeance">📄 Reçus d'échéancier édités</SelectItem>
                      <SelectItem value="recu_acquitte">✅ Contributions acquittées</SelectItem>
                      <SelectItem value="en_attente_recu">⏳ En attente de reçu</SelectItem>
                      <SelectItem value="avec_paiements">💰 Avec paiements partiels</SelectItem>
                    </SelectContent>
                  </Select>
                  {filterRecuStatus !== 'all' && (
                    <Badge className="bg-emerald-600 text-white font-semibold">
                      Filtre actif
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Onglets - Design Ultra Moderne */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2">
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('dashboard')}
            data-testid="tab-dashboard"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'dashboard'
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-200 scale-[1.02]'
                : 'text-gray-600 hover:text-indigo-700 hover:bg-indigo-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-white/20' : 'bg-indigo-100 group-hover:bg-indigo-200'}`}>
              <LayoutDashboard className={`h-4 w-4 ${activeTab === 'dashboard' ? 'text-white' : 'text-indigo-600'}`} />
            </div>
            <span>Tableau de bord</span>
          </button>
          
          <button
            onClick={() => setActiveTab('attente')}
            data-testid="tab-attente"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'attente'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-200 scale-[1.02]'
                : 'text-gray-600 hover:text-amber-700 hover:bg-amber-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'attente' ? 'bg-white/20' : 'bg-amber-600 group-hover:bg-amber-700'}`}>
              <Clock className={`h-4 w-4 ${activeTab === 'attente' ? 'text-white' : 'text-amber-600'}`} />
            </div>
            <span>En attente</span>
            <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'attente' ? 'bg-white/20 text-white' : 'bg-amber-600 text-white'
            }`}>
              {documentsEnAttente.length}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('partiels')}
            data-testid="tab-partiels"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'partiels'
                ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-200 scale-[1.02]'
                : 'text-gray-600 hover:text-purple-700 hover:bg-purple-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'partiels' ? 'bg-white/20' : 'bg-purple-600 group-hover:bg-purple-700'}`}>
              <PieChart className={`h-4 w-4 ${activeTab === 'partiels' ? 'text-white' : 'text-purple-600'}`} />
            </div>
            <span>Paiement partiel</span>
            <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'partiels' ? 'bg-white/20 text-white' : 'bg-purple-600 text-white'
            }`}>
              {dashboardStats.paiementsPartielsEnCours}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('valides')}
            data-testid="tab-valides"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'valides'
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-200 scale-[1.02]'
                : 'text-gray-600 hover:text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'valides' ? 'bg-white/20' : 'bg-emerald-600 group-hover:bg-emerald-700'}`}>
              <CheckCircle className={`h-4 w-4 ${activeTab === 'valides' ? 'text-white' : 'text-emerald-600'}`} />
            </div>
            <span>Validés</span>
            <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'valides' ? 'bg-white/20 text-white' : 'bg-emerald-600 text-white'
            }`}>
              {documentsValides.length}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('archives')}
            data-testid="tab-archives"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'archives'
                ? 'bg-gradient-to-r from-slate-600 to-gray-700 text-white shadow-lg shadow-slate-200 scale-[1.02]'
                : 'text-gray-600 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'archives' ? 'bg-white/20' : 'bg-slate-600 group-hover:bg-slate-700'}`}>
              <Building className={`h-4 w-4 ${activeTab === 'archives' ? 'text-white' : 'text-slate-600'}`} />
            </div>
            <span>Archivés GED</span>
            <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'archives' ? 'bg-white/20 text-white' : 'bg-slate-600 text-white'
            }`}>
              {dashboardStats.totalArchives || 0}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('comptabilite')}
            data-testid="tab-comptabilite"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'comptabilite'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-200 scale-[1.02]'
                : 'text-gray-600 hover:text-cyan-700 hover:bg-cyan-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'comptabilite' ? 'bg-white/20' : 'bg-cyan-100 group-hover:bg-cyan-200'}`}>
              <BarChart3 className={`h-4 w-4 ${activeTab === 'comptabilite' ? 'text-white' : 'text-cyan-600'}`} />
            </div>
            <span>Comptabilité & Exports</span>
          </button>
          
          <button
            onClick={() => setActiveTab('acomptes')}
            data-testid="tab-acomptes"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'acomptes'
                ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg shadow-rose-200 scale-[1.02]'
                : 'text-gray-600 hover:text-rose-700 hover:bg-rose-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'acomptes' ? 'bg-white/20' : 'bg-rose-600 group-hover:bg-rose-700'}`}>
              <Wallet className={`h-4 w-4 ${activeTab === 'acomptes' ? 'text-white' : 'text-rose-600'}`} />
            </div>
            <span>Acomptes</span>
            <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'acomptes' ? 'bg-white/20 text-white' : 'bg-rose-600 text-white'
            }`}>
              {getAllAcomptesESS.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('sepa-rcd')}
            data-testid="tab-sepa-rcd"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'sepa-rcd'
                ? 'bg-gradient-to-r from-[#0a3d62] to-[#DEA900] text-white shadow-lg shadow-[#DEA900]/30 scale-[1.02]'
                : 'text-gray-600 hover:text-[#0a3d62] hover:bg-amber-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'sepa-rcd' ? 'bg-white/20' : 'bg-[#DEA900] group-hover:bg-[#f5cc3e]'}`}>
              <Banknote className={`h-4 w-4 ${activeTab === 'sepa-rcd' ? 'text-white' : 'text-[#0a3d62]'}`} />
            </div>
            <span>SEPA/CB RCD Solidaire</span>
          </button>

          <button
            onClick={() => setActiveTab('taux-encaissement')}
            data-testid="tab-taux-encaissement"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'taux-encaissement'
                ? 'bg-gradient-to-r from-[#0a3d62] to-[#DEA900] text-white shadow-lg scale-[1.02]'
                : 'text-gray-600 hover:text-[#0a3d62] hover:bg-amber-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'taux-encaissement' ? 'bg-white/20' : 'bg-amber-100 group-hover:bg-amber-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'taux-encaissement' ? 'text-white' : 'text-[#0a3d62]'}`} />
            </div>
            <span>Taux d&apos;encaissement</span>
          </button>

          <button
            onClick={() => setActiveTab('a-reclamer')}
            data-testid="tab-a-reclamer"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'a-reclamer'
                ? 'bg-gradient-to-r from-[#0a3d62] to-[#DEA900] text-white shadow-lg scale-[1.02]'
                : 'text-gray-600 hover:text-[#0a3d62] hover:bg-amber-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'a-reclamer' ? 'bg-white/20' : 'bg-amber-100 group-hover:bg-amber-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'a-reclamer' ? 'text-white' : 'text-[#0a3d62]'}`} />
            </div>
            <span>À réclamer</span>
          </button>

          <button
            onClick={() => setActiveTab('impayes')}
            data-testid="tab-impayes"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'impayes'
                ? 'bg-gradient-to-r from-[#0a3d62] to-rose-600 text-white shadow-lg scale-[1.02]'
                : 'text-gray-600 hover:text-rose-700 hover:bg-rose-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'impayes' ? 'bg-white/20' : 'bg-rose-100 group-hover:bg-rose-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'impayes' ? 'text-white' : 'text-rose-700'}`} />
            </div>
            <span>Impayés</span>
          </button>

          <button
            onClick={() => setActiveTab('rejets-mandats')}
            data-testid="tab-rejets-mandats"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'rejets-mandats'
                ? 'bg-gradient-to-r from-rose-600 to-[#0a3d62] text-white shadow-lg scale-[1.02]'
                : 'text-gray-600 hover:text-rose-700 hover:bg-rose-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'rejets-mandats' ? 'bg-white/20' : 'bg-rose-100 group-hover:bg-rose-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'rejets-mandats' ? 'text-white' : 'text-rose-600'}`} />
            </div>
            <span>Rejets SEPA à représenter</span>
          </button>

          <button
            onClick={() => setActiveTab('recus-mandats')}
            data-testid="tab-recus-mandats"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'recus-mandats'
                ? 'bg-gradient-to-r from-[#0a3d62] to-emerald-600 text-white shadow-lg scale-[1.02]'
                : 'text-gray-600 hover:text-[#0a3d62] hover:bg-emerald-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'recus-mandats' ? 'bg-white/20' : 'bg-emerald-100 group-hover:bg-emerald-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'recus-mandats' ? 'text-white' : 'text-emerald-700'}`} />
            </div>
            <span>Reçus mandats SEPA</span>
          </button>

          <button
            onClick={() => setActiveTab('domium-paiements')}
            data-testid="tab-domium-paiements"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'domium-paiements'
                ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-200 scale-[1.02]'
                : 'text-gray-600 hover:text-violet-700 hover:bg-violet-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'domium-paiements' ? 'bg-white/20' : 'bg-violet-100 group-hover:bg-violet-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'domium-paiements' ? 'text-white' : 'text-violet-600'}`} />
            </div>
            <span>Paiements DOS-DOMIUM</span>
          </button>

          <button
            onClick={() => setActiveTab('generer-recu')}
            data-testid="tab-generer-recu"
            className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
              activeTab === 'generer-recu'
                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-200 scale-[1.02]'
                : 'text-gray-600 hover:text-orange-700 hover:bg-orange-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === 'generer-recu' ? 'bg-white/20' : 'bg-orange-100 group-hover:bg-orange-200'}`}>
              <Receipt className={`h-4 w-4 ${activeTab === 'generer-recu' ? 'text-white' : 'text-orange-600'}`} />
            </div>
            <span>Generer un Recu</span>
          </button>
        </div>
      </div>
    </>
  );
}
